import { useEffect, useState } from 'react';
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp, getCountFromServer, collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Loader2, Plus, BookOpen, Users, ArrowUpRight, Calendar, ClipboardCheck, Search, FileText, ChevronRight, Zap, ArrowRight, TrendingUp, Clock, AlertTriangle, Timer } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import WorkspaceLoader from './WorkspaceLoader';
import { subjectCategories } from './subjectCategories';
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899'];

const IndependentDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [stats, setStats] = useState({
    lessons: 0,
    students: 0,
    classes: 0,
    assessments: 0,
    research: 0,
    totalQuestions: 0
  });
  const [recentCourses, setRecentCourses] = useState<{ id: string; title: string; createdAt: any; category?: string }[]>([]);
  const [courseProgress, setCourseProgress] = useState<Record<string, number>>({});
  const [recentResearch, setRecentResearch] = useState<{ id: string; title?: string; subject?: string; createdAt: any; type?: string }[]>([]);
  const [recentExams, setRecentExams] = useState<{ id: string; title?: string; subject?: string; createdAt: any; questions?: any[] }[]>([]);
  const auth = getAuth();

  useEffect(() => {
    const bootstrapWorkspace = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const workspaceRef = doc(db, "teaching_workspaces", user.uid);
        const workspaceSnap = await getDoc(workspaceRef);

        if (workspaceSnap.exists()) {
          const data = workspaceSnap.data();
          setFirstName(data.displayName?.split(' ')[0] || "Teacher");
          setPlan(data.subscription?.plan || "individual_trial");
          setStatus(data.subscription?.status || "trial");
          if (data.subscription?.expiry?.toDate) {
            setExpiry(data.subscription.expiry.toDate());
          } else if (data.subscription?.expiry) {
            const d = data.subscription.expiry instanceof Date ? data.subscription.expiry : null;
            setExpiry(d);
          }
        } else {
          let trialDays = 14; 
          const configRef = doc(db, "system_config", "independent_settings");
          const configSnap = await getDoc(configRef);
          if (configSnap.exists() && configSnap.data().trialDays) {
              trialDays = configSnap.data().trialDays;
          }
          
          const now = new Date();
          const expiryDate = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000);

          const name = user.displayName || "Teacher";
          await setDoc(workspaceRef, {
            ownerId: user.uid,
            displayName: `${name}'s Workspace`,
            email: user.email,
            createdAt: serverTimestamp(),
            subscription: {
              status: 'trial',
              plan: 'individual_trial',
              expiry: Timestamp.fromDate(expiryDate)
            }
          });
          setFirstName(name.split(' ')[0]);
          setPlan('individual_trial');
          setStatus('trial');
          setExpiry(expiryDate);
        }

        // Fetch counts
        try {
          const lessonsCol = collection(db, `teaching_workspaces/${user.uid}/courses`);
          const studentsCol = collection(db, `teaching_workspaces/${user.uid}/students`);
          const classesCol = collection(db, `teaching_workspaces/${user.uid}/classes`);
          const assessmentsCol = collection(db, `teaching_workspaces/${user.uid}/assessments`);
          const researchCol = collection(db, `teaching_workspaces/${user.uid}/savedResearch`);

          const [lessonsSnap, studentsSnap, classesSnap, assessmentsSnap, researchSnap] = await Promise.all([
            getCountFromServer(lessonsCol),
            getCountFromServer(studentsCol),
            getCountFromServer(classesCol),
            getCountFromServer(assessmentsCol),
            getCountFromServer(researchCol)
          ]);

          setStats(prev => ({
            ...prev,
            lessons: lessonsSnap.data().count,
            students: studentsSnap.data().count,
            classes: classesSnap.data().count,
            assessments: assessmentsSnap.data().count,
            research: researchSnap.data().count
          }));
        } catch (statError) {
          console.error("Error fetching stats:", statError);
        }

        // Fetch recent courses (last 4)
        try {
          const coursesQ = query(
            collection(db, `teaching_workspaces/${user.uid}/courses`),
            orderBy('createdAt', 'desc'),
            limit(4)
          );
          const coursesSnap = await getDocs(coursesQ);
          const courses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));
          setRecentCourses(courses);

          // Fetch reading progress for each recent course
          const progressMap: Record<string, number> = {};
          await Promise.all(courses.map(async (c: any) => {
            try {
              const progSnap = await getDocs(
                collection(db, `teaching_workspaces/${user.uid}/courses/${c.id}/reading_progress`)
              );
              let max = 0;
              progSnap.docs.forEach(d => {
                max = Math.max(max, Number(d.data().progressPercent || 0));
              });
              progressMap[c.id] = Math.round(max);
            } catch {}
          }));
          setCourseProgress(progressMap);
        } catch (e) {
          console.error("Error fetching recent courses:", e);
        }

        // Fetch recent research (last 3)
        try {
          const researchQ = query(
            collection(db, `teaching_workspaces/${user.uid}/savedResearch`),
            orderBy('createdAt', 'desc'),
            limit(3)
          );
          const researchSnap = await getDocs(researchQ);
          setRecentResearch(researchSnap.docs.map(d => ({ id: d.id, ...d.data() } as any)));
        } catch (e) {
          console.error("Error fetching recent research:", e);
        }

        // Fetch total questions across assessments
        try {
          const assessSnap = await getDocs(collection(db, `teaching_workspaces/${user.uid}/assessments`));
          let totalQ = 0;
          assessSnap.docs.forEach(d => {
            const data = d.data();
            if (Array.isArray(data.questions)) totalQ += data.questions.length;
          });
          setStats(prev => ({ ...prev, totalQuestions: totalQ }));
        } catch (e) {
          console.error("Error counting questions:", e);
        }

      } catch (error) {
        console.error("Error loading workspace:", error);
      } finally {
        setLoading(false);
      }
    };

    bootstrapWorkspace();
  }, [auth.currentUser]);

  if (loading) {
    return <WorkspaceLoader />;
  }

  const isTier2 = plan.toLowerCase().includes('tier2');

  const daysLeft = expiry
    ? Math.max(0, Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const isExpiryUrgent = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;
  const isExpired = daysLeft !== null && daysLeft <= 0;

  const statCards = isTier2
    ? [
        { label: "STUDENTS", value: stats.students, icon: Users, iconBg: "bg-blue-50 dark:bg-blue-500/10", iconColor: "text-blue-600 dark:text-blue-400", glow: "bg-blue-500/20", link: "/dashboard/students", subtitle: "Enrolled learners" },
        { label: "LESSONS", value: stats.lessons, icon: BookOpen, iconBg: "bg-emerald-50 dark:bg-emerald-500/10", iconColor: "text-emerald-600 dark:text-emerald-400", glow: "bg-emerald-500/20", link: "/dashboard/lessons", subtitle: "Lesson plans" },
        { label: "CLASSES", value: stats.classes, icon: Calendar, iconBg: "bg-amber-50 dark:bg-amber-500/10", iconColor: "text-amber-600 dark:text-amber-400", glow: "bg-amber-500/20", link: "/dashboard/classes", subtitle: "Active classes" },
        { label: "ASSESSMENTS", value: stats.assessments, icon: ClipboardCheck, iconBg: "bg-rose-50 dark:bg-rose-500/10", iconColor: "text-rose-600 dark:text-rose-400", glow: "bg-rose-500/20", link: "/dashboard/assessment", subtitle: "Tests & exams" },
      ]
    : [
        { label: "LESSONS", value: stats.lessons, icon: BookOpen, iconBg: "bg-blue-50 dark:bg-blue-500/10", iconColor: "text-blue-600 dark:text-blue-400", glow: "bg-blue-500/20", link: "/dashboard/lessons", subtitle: "Lesson plans" },
        { label: "ASSESSMENTS", value: stats.assessments, icon: ClipboardCheck, iconBg: "bg-amber-50 dark:bg-amber-500/10", iconColor: "text-amber-600 dark:text-amber-400", glow: "bg-amber-500/20", link: "/dashboard/assessment", subtitle: "Tests & exams" },
        { label: "QUESTIONS", value: stats.totalQuestions, icon: FileText, iconBg: "bg-emerald-50 dark:bg-emerald-500/10", iconColor: "text-emerald-600 dark:text-emerald-400", glow: "bg-emerald-500/20", link: "/dashboard/assessment", subtitle: "Question bank" },
        { label: "RESEARCH", value: stats.research, icon: Search, iconBg: "bg-rose-50 dark:bg-rose-500/10", iconColor: "text-rose-600 dark:text-rose-400", glow: "bg-rose-500/20", link: "/dashboard/research", subtitle: "Saved research" },
      ];

  const chartData = isTier2
    ? [
        { name: 'Lessons', val: stats.lessons },
        { name: 'Students', val: stats.students },
        { name: 'Classes', val: stats.classes },
        { name: 'Exams', val: stats.assessments },
      ]
    : [
        { name: 'Lessons', val: stats.lessons },
        { name: 'Exams', val: stats.assessments },
        { name: 'Research', val: stats.research },
      ];

  const stagger: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.06 } }
  };

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } }
  };

  const formatDate = (ts: any) => {
    if (!ts) return '';
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
    if (!d) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="min-h-screen pb-8 px-3 sm:px-0">

      {/* ── Header ── */}
      <motion.div variants={fadeUp} className="flex justify-end mb-6 sm:mb-8">
        <Button
          onClick={() => navigate('/dashboard/lessons/new')}
          className="w-full sm:w-auto shrink-0 bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 font-bold text-[10px] uppercase tracking-widest px-5 h-9 rounded-lg transition-all active:scale-[0.97] shadow-md shadow-indigo-500/25 hover:shadow-lg hover:shadow-indigo-500/30"
        >
          <Plus className="mr-2 h-4 w-4" /> New Lesson
        </Button>
      </motion.div>

      {/* ── Expiry urgency banner ── */}
      {isExpiryUrgent && (
        <motion.div variants={fadeUp}>
          <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3.5 sm:p-4 rounded-xl bg-amber-50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/20">
            <div className="flex items-center gap-3 flex-1 min-w-0 w-full sm:w-auto">
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/10 flex items-center justify-center shrink-0">
                <Timer className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-amber-800 dark:text-amber-300">
                  Your {plan.replace(/_/g, ' ')} plan expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}
                </p>
                <p className="text-[10px] text-amber-600/70 dark:text-amber-400/50 mt-0.5">
                  Renew now to keep uninterrupted access to your workspace.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dashboard/billing')}
              className="w-full sm:w-auto shrink-0 bg-amber-600 hover:bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest px-4 h-8 rounded-lg transition-all active:scale-[0.97]"
            >
              Renew
            </Button>
          </div>
        </motion.div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {statCards.map((card) => (
          <motion.div key={card.label} variants={fadeUp} whileHover={{ y: -5 }}>
            <button
              onClick={() => card.link && navigate(card.link)}
              className="group relative w-full text-left bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl p-3.5 sm:p-5 hover:border-slate-300 dark:hover:border-white/[0.12] hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
            >
              <div className={`absolute -top-8 -right-8 w-24 h-24 ${card.glow} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              <div className="relative flex items-start justify-between mb-2.5 sm:mb-3">
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${card.iconBg} flex items-center justify-center`}>
                  <card.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${card.iconColor}`} strokeWidth={2} />
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 dark:text-white/10 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="relative mb-2.5 sm:mb-3">
                <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-white/30 mb-0.5">{card.label}</p>
                <p className="text-xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                  {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                </p>
              </div>
              <div className="relative flex items-center justify-between pt-2 sm:pt-2.5 border-t border-slate-100 dark:border-white/[0.04]">
                <span className="text-[9px] font-semibold text-slate-400 dark:text-white/25 uppercase tracking-wider truncate">{card.subtitle}</span>
                <span className="hidden sm:inline text-[9px] font-bold text-indigo-500 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">View →</span>
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* ── Recent Courses ── */}
      {recentCourses.length > 0 && (
        <motion.div variants={fadeUp} className="mb-6 sm:mb-8">
          <div className="bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl">
            <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Recent Courses</h3>
                <p className="text-[10px] text-slate-400 dark:text-white/30 mt-0.5 uppercase tracking-wider font-semibold hidden xs:block">Continue where you left off</p>
              </div>
              <button
                onClick={() => navigate('/dashboard/lessons')}
                className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-white/30 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors px-2 py-1 rounded hover:bg-slate-50 dark:hover:bg-white/[0.03] shrink-0"
              >
                View all
              </button>
            </div>
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {recentCourses.map((course) => {
                const prog = courseProgress[course.id] ?? 0;
                const cat = subjectCategories.find((c: any) => c.value === course.category);
                const Icon = cat?.icon || BookOpen;
                const iconColor = cat?.color || 'text-indigo-500';
                const theme = cat ? `${cat.color} ${cat.bg} ${cat.border}` : 'text-slate-600 bg-slate-100 dark:bg-slate-800 border-slate-200';
                const categoryLabel = cat?.label || course.category || 'Course';
                const schoolName = (course as any).schools?.[0] || (course as any).school || 'Workspace';
                return (
                  <motion.div whileHover={{ y: -5 }} key={course.id}>
                    <button
                      onClick={() => navigate(`/dashboard/lessons/view/${course.id}`)}
                      className="text-left w-full p-3.5 sm:p-4 rounded-xl border border-slate-100 dark:border-white/[0.04] hover:shadow-xl hover:border-primary/20 dark:hover:border-white/[0.1] transition-all bg-white dark:bg-white/5 group/card flex flex-col h-full"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`p-2 rounded-lg ${theme} shrink-0`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{course.title || 'Untitled'}</p>
                            <p className="text-[9px] font-semibold text-slate-400 dark:text-white/30 uppercase tracking-wider truncate">{categoryLabel}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 dark:text-white/20 group-hover/card:text-indigo-500 dark:group-hover/card:text-indigo-400 group-hover/card:translate-x-0.5 transition-all shrink-0" />
                      </div>
                      <div className="space-y-1.5 mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-semibold text-slate-400 dark:text-white/30 uppercase tracking-wider">Progress</span>
                          <span className="text-[10px] font-black text-slate-500 dark:text-white/40 tabular-nums">{Math.round(prog)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${prog}%` }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="h-full rounded-full bg-indigo-500"
                          />
                        </div>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-2 border-t border-slate-100 dark:border-white/[0.04] gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Users className="w-3 h-3 text-slate-300 dark:text-white/15 shrink-0" />
                          <span className="text-[9px] font-semibold text-slate-400 dark:text-white/25 truncate">{schoolName}</span>
                        </div>
                        <span className="text-[9px] text-slate-400 dark:text-white/20 font-semibold shrink-0">{formatDate(course.createdAt)}</span>
                      </div>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Chart + Quick Actions ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-8">

        {/* Chart */}
        <motion.div variants={fadeUp} className="lg:col-span-3">
          <div className="bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl h-full">
            <div className="flex items-center justify-between px-5 pt-5 pb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Content Overview</h3>
                <p className="text-[10px] text-slate-400 dark:text-white/30 mt-0.5 uppercase tracking-wider font-semibold">
                  {isTier2 ? 'Workspace breakdown' : 'Your generated content'}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {COLORS.slice(0, chartData.length).map((c, i) => (
                  <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div className="px-2 pb-4 h-[220px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 12, right: 16, bottom: 0, left: -16 }}>
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'currentColor', opacity: 0.35, fontSize: 10, fontWeight: 700 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'currentColor', opacity: 0.25, fontSize: 10 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(99,102,241,0.04)' }}
                    contentStyle={{
                      borderRadius: '10px',
                      border: '1px solid rgba(0,0,0,0.06)',
                      backgroundColor: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                    }}
                    labelStyle={{ color: '#94a3b8', fontSize: '9px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  />
                  <Bar dataKey="val" radius={[6, 6, 0, 0]} maxBarSize={48}>
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions + AI Search */}
        <motion.div variants={fadeUp} className="lg:col-span-2 flex flex-col gap-4">
          {/* AI Search - Prominent */}
          <button
            onClick={() => navigate('/dashboard/utilities/search')}
            className="w-full flex items-center gap-3 p-4 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-all active:scale-[0.99] text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-white">Quick Search</p>
              <p className="text-[10px] text-white/60 mt-0.5 truncate">AI assistant with 15+ workflow modes</p>
            </div>
            <ArrowRight className="w-4 h-4 text-white/50 shrink-0" />
          </button>

          {/* Standard quick actions */}
          <div className="bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl flex-1 flex flex-col">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Quick Actions</h3>
              <p className="text-[10px] text-slate-400 dark:text-white/30 mt-0.5 uppercase tracking-wider font-semibold">Jump to a task</p>
            </div>
            <div className="px-4 pb-4 space-y-2 flex-1">
              {[
                { label: 'Draft a Lesson Plan', icon: BookOpen, color: 'text-blue-500', hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-500/[0.06]', path: '/dashboard/lessons/new' },
                { label: 'Generate Assessment', icon: ClipboardCheck, color: 'text-amber-500', hoverBg: 'hover:bg-amber-50 dark:hover:bg-amber-500/[0.06]', path: '/dashboard/assessment' },
                ...(isTier2
                  ? [{ label: 'Manage Enrollment', icon: Users, color: 'text-emerald-500', hoverBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/[0.06]', path: '/dashboard/students' }]
                  : [{ label: 'Browse Resources', icon: FileText, color: 'text-rose-500', hoverBg: 'hover:bg-rose-50 dark:hover:bg-rose-500/[0.06]', path: '/dashboard/resources' }]
                ),
              ].map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border border-slate-100 dark:border-white/[0.04] text-left transition-all duration-200 group/action ${action.hoverBg} hover:border-slate-200 dark:hover:border-white/[0.1] hover:shadow-sm`}
                >
                  <div className={`w-8 h-8 rounded-md bg-slate-50 dark:bg-white/[0.03] flex items-center justify-center ${action.color} group-hover/action:scale-105 transition-transform`}>
                    <action.icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <span className="flex-1 text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{action.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 dark:text-white/15 group-hover/action:text-slate-500 dark:group-hover/action:text-white/40 group-hover/action:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Recent Research ── */}
      {recentResearch.length > 0 && (
        <motion.div variants={fadeUp} className="mb-6 sm:mb-8">
          <div className="bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl">
            <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Recent Research</h3>
                <p className="text-[10px] text-slate-400 dark:text-white/30 mt-0.5 uppercase tracking-wider font-semibold hidden xs:block">Your latest exam and research outputs</p>
              </div>
              <button
                onClick={() => navigate('/dashboard/research')}
                className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-white/30 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors px-2 py-1 rounded hover:bg-slate-50 dark:hover:bg-white/[0.03] shrink-0"
              >
                View all
              </button>
            </div>
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recentResearch.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate('/dashboard/research')}
                  className="text-left p-3.5 rounded-lg border border-slate-100 dark:border-white/[0.04] hover:border-slate-200 dark:hover:border-white/[0.1] hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-500/10 flex items-center justify-center shrink-0">
                      <ClipboardCheck className="w-3 h-3 text-teal-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-900 dark:text-white truncate">{item.title || item.subject || 'Research output'}</p>
                  </div>
                  <p className="text-[9px] text-slate-400 dark:text-white/20 font-semibold">
                    {formatDate(item.createdAt)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Plan & Upgrade ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-3 sm:px-0">
        {/* Current Plan */}
        <motion.div variants={fadeUp}>
          <div className="bg-white dark:bg-[#0c1120] border border-slate-200 dark:border-white/[0.06] rounded-xl p-5 h-full">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                <Zap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" strokeWidth={2} />
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Current Plan</h3>
            </div>

            <div className="flex items-center justify-between p-3.5 rounded-lg bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
              <div>
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-white/30 mb-0.5">Active subscription</p>
                <p className="text-sm font-bold text-slate-900 dark:text-white capitalize">{plan.replace(/_/g, ' ')}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[8px] px-2 py-1 rounded-full font-black uppercase tracking-widest ${
                  status === 'active'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/20'
                    : status === 'trial'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/20'
                    : 'bg-slate-500/10 text-slate-500 dark:text-slate-400 ring-1 ring-slate-500/20'
                }`}>
                  {status}
                </span>
                <button
                  onClick={() => navigate('/dashboard/billing')}
                  className="text-[9px] font-black uppercase tracking-widest text-indigo-500 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                  Manage
                </button>
              </div>
            </div>

            {daysLeft !== null && daysLeft > 0 && (
              <div className="mt-3 flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
                <Clock className="w-3.5 h-3.5 text-slate-400 dark:text-white/25" />
                <p className="text-[10px] text-slate-500 dark:text-white/40 font-semibold">
                  {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Upgrade CTA — only when not tier2 and not in urgent banner */}
        {!isTier2 && !isExpiryUrgent && (
          <motion.div variants={fadeUp}>
            <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 rounded-xl p-5 h-full">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-indigo-600 dark:text-indigo-400" strokeWidth={2} />
                </div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Upgrade to Tier 2</h3>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
                Unlock Student Management, Classes, Advanced Analytics, and Custom Branding.
              </p>
              <Button
                onClick={() => navigate('/dashboard/billing')}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-[9px] font-black uppercase tracking-[0.2em] h-9 rounded-lg transition-all active:scale-[0.98]"
              >
                View Premium Features <ArrowRight className="ml-2 w-3.5 h-3.5" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Spacer if tier2 or urgent banner shown */}
        {isTier2 && <motion.div variants={fadeUp} />}
      </div>
    </motion.div>
  );
};

export default IndependentDashboard;
